import { api } from "./api";
import { getAll as get } from "./pagination";
export interface Curriculum {
  id: number;
  name: string;
  curriculum_type: string;
  version: string;
  description?: string;
  country: string;
  language: string;
  education_level: string;
  start_date: string;
  end_date?: string | null;
  is_active: boolean;
  is_mandatory?: boolean;
  curriculum_authority?: string | null;
  objectives?: string | null;
  assessment_criteria?: string | null;
  resources_required?: string | null;
  notes?: string | null;
}
/** What deleting a record would touch, by kind -- sent when one record is opened. */
export type InUse = Record<string, number>;
export interface Subject {
  id: number;
  curriculum: number;
  curriculum_name: string;
  name: string;
  code: string;
  category: string;
  description?: string;
  weekly_periods: number;
  assessment_weight: string;
  passing_grade: string;
  is_compulsory: boolean;
  is_examinable: boolean;
  is_active: boolean;
  credit_hours?: number;
  prerequisite_names?: string[];
  textbooks?: string | null;
  reference_materials?: string | null;
  equipment_required?: string | null;
  notes?: string | null;
}
/**
 * Decimal fields arrive from DRF as strings but are accepted as numbers on
 * write, so the write shape is deliberately wider than the read shape.
 */
export type SubjectInput = Omit<
  Partial<Subject>,
  "assessment_weight" | "passing_grade"
> & {
  assessment_weight?: number | string;
  passing_grade?: number | string;
};

export interface Grade {
  id: number;
  curriculum: number;
  curriculum_name: string;
  name: string;
  level: number;
  academic_year: number;
  academic_year_name: string;
}
export interface Syllabus {
  id: number;
  subject: number;
  subject_name: string;
  subject_code?: string;
  grade: number;
  grade_name: string;
  class_level?: string;
  curriculum_name: string;
  title: string;
  version: string;
  description?: string;
  start_date: string;
  end_date?: string;
  is_active: boolean;
  learning_outcomes?: string;
  assessment_objectives?: string;
  recommended_books?: string;
  teaching_materials?: string;
  digital_resources?: string;
  assessment_methods?: string;
  grading_criteria?: string;
  notes?: string;
  /** Whether the viewer may change its topics and lessons (the server's own rule). */
  can_edit?: boolean;
  updated_at?: string;
}
export interface Topic {
  id: number;
  syllabus: number;
  syllabus_title: string;
  subject_name: string;
  grade_name: string;
  name: string;
  description?: string;
  duration_weeks: number;
  difficulty_level: string;
  learning_objectives?: string;
  key_concepts?: string;
  skills_to_develop?: string;
  assessment_methods?: string;
  evaluation_criteria?: string;
  teaching_strategies?: string;
  learning_activities?: string;
  resources_needed?: string;
  order: number;
  is_active: boolean;
}
export interface Lesson {
  id: number;
  topic: number;
  topic_name: string;
  syllabus_title: string;
  subject_name: string;
  grade_name: string;
  title: string;
  description?: string;
  duration_minutes: number;
  lesson_type: string;
  learning_objectives?: string;
  success_criteria?: string;
  introduction?: string;
  main_activities?: string;
  conclusion?: string;
  homework?: string;
  teaching_materials?: string;
  assessment_methods?: string;
  order: number;
  is_active: boolean;
}
export interface Progress {
  id: number;
  syllabus: number;
  syllabus_title: string;
  subject_name: string;
  grade_name: string;
  class_group: number;
  class_name: string;
  teacher: number;
  teacher_name: string;
  topic: number;
  topic_name: string;
  topic_order: number;
  topic_weeks: number;
  /** Still under way after the weeks the topic was planned for. */
  is_behind: boolean;
  start_date: string;
  completion_date?: string | null;
  status: string;
  progress_percentage: string;
  notes?: string | null;
  achievements?: string | null;
  challenges?: string | null;
  updated_at: string;
}

/** One class's progress through one syllabus (curriculum/progress/coverage/). */
export interface CoverageRow {
  id: string;
  syllabus: number;
  syllabus_title: string;
  subject_name: string;
  grade_name: string;
  class_id: number;
  class_name: string;
  topic_count: number;
  completed: number;
  in_progress: number;
  behind: number;
  not_started: number;
  coverage: number;
  last_updated: string | null;
  teachers: string[];
  /** The signed-in teacher teaches this syllabus to this class, so records its coverage. */
  can_record: boolean;
}
export interface CurriculumStats {
  total_curricula: number;
  active_curricula: number;
  total_subjects: number;
  total_syllabi: number;
  total_topics: number;
  completed_topics: number;
  in_progress_topics: number;
  curriculum_completion_rate: number;
}

export const curriculumService = {
  getCurricula: () => get<Curriculum>("/curriculum/curricula/"),
  /** Active and inactive, for the Academics register; pickers use getCurricula. */
  getAllCurricula: () => get<Curriculum>("/curriculum/curricula/", { include_inactive: 1 }),
  getCurriculum: async (id: number) =>
    (await api.get(`/curriculum/curricula/${id}/`)).data as Curriculum & { in_use: InUse },
  updateCurriculum: async (id: number, data: Partial<Curriculum>) =>
    (await api.patch(`/curriculum/curricula/${id}/`, data)).data as Curriculum,
  deleteCurriculum: async (id: number) => {
    await api.delete(`/curriculum/curricula/${id}/`);
  },
  getAllSubjects: () => get<Subject>("/curriculum/subjects/", { include_inactive: 1 }),
  getSubject: async (id: number) =>
    (await api.get(`/curriculum/subjects/${id}/`)).data as Subject & { in_use: InUse },
  createCurriculum: async (data: Partial<Curriculum>) =>
    (await api.post("/curriculum/curricula/", data)).data,
  getSubjects: (params?: Record<string, unknown>) =>
    get<Subject>("/curriculum/subjects/", params),
  createSubject: async (data: SubjectInput) =>
    (await api.post("/curriculum/subjects/", data)).data,
  updateSubject: async (id: number, data: SubjectInput) =>
    (await api.patch(`/curriculum/subjects/${id}/`, data)).data,
  deleteSubject: async (id: number) => {
    await api.delete(`/curriculum/subjects/${id}/`);
  },
  getGrades: () => get<Grade>("/curriculum/grades/"),
  createGrade: async (data: Partial<Grade>) =>
    (await api.post("/curriculum/grades/", data)).data,
  getSyllabi: () => get<Syllabus>("/curriculum/syllabi/"),
  /** Active and inactive, for Syllabus Management; other screens use getSyllabi. */
  getAllSyllabi: () => get<Syllabus>("/curriculum/syllabi/", { include_inactive: 1 }),
  getSyllabus: async (id: number) =>
    (await api.get(`/curriculum/syllabi/${id}/`)).data as Syllabus & { in_use: InUse },
  createSyllabus: async (data: Partial<Syllabus>) =>
    (await api.post("/curriculum/syllabi/", data)).data,
  updateSyllabus: async (id: number, data: Partial<Syllabus>) =>
    (await api.patch(`/curriculum/syllabi/${id}/`, data)).data,
  deleteSyllabus: async (id: number) => {
    await api.delete(`/curriculum/syllabi/${id}/`);
  },
  getTopics: () => get<Topic>("/curriculum/topics/"),
  createTopic: async (data: Partial<Topic>) =>
    (await api.post("/curriculum/topics/", data)).data,
  updateTopic: async (id: number, data: Partial<Topic>) =>
    (await api.patch(`/curriculum/topics/${id}/`, data)).data,
  deleteTopic: async (id: number) => {
    await api.delete(`/curriculum/topics/${id}/`);
  },
  getLessons: () => get<Lesson>("/curriculum/lessons/"),
  createLesson: async (data: Partial<Lesson>) =>
    (await api.post("/curriculum/lessons/", data)).data,
  updateLesson: async (id: number, data: Partial<Lesson>) =>
    (await api.patch(`/curriculum/lessons/${id}/`, data)).data,
  deleteLesson: async (id: number) => {
    await api.delete(`/curriculum/lessons/${id}/`);
  },
  getProgress: () => get<Progress>("/curriculum/progress/"),
  /** One class's records for one syllabus. */
  getClassProgress: (syllabus: number, classId: number) => get<Progress>("/curriculum/progress/", { syllabus, class: classId }),
  getCoverage: async () => (await api.get("/curriculum/progress/coverage/")).data as CoverageRow[],
  /** A syllabus's topics, in teaching order. */
  getSyllabusTopics: (syllabus: number) => get<Topic>("/curriculum/topics/", { syllabus }),
  startTopic: async (data: {
    syllabus_id: number;
    class_id: number;
    topic_id: number;
  }) => (await api.post("/curriculum/progress/start-topic/", data)).data as Progress,
  updateProgress: async ({ id, percentage, notes }: { id: number; percentage: number; notes?: string }) =>
    (await api.post(`/curriculum/progress/${id}/update/`, { progress_percentage: percentage, ...(notes !== undefined ? { notes } : {}) })).data as Progress,
  /** Only what the teacher wrote: the server stores nothing on their behalf. */
  completeTopic: async ({ id, achievements, challenges }: { id: number; achievements: string; challenges: string }) =>
    (await api.post(`/curriculum/progress/${id}/complete/`, { achievements, challenges })).data as Progress,
  getStats: async () =>
    (await api.get("/curriculum/statistics/")).data as CurriculumStats,
};
